export const AUDIENCE_PRESETS = [
  {
    key: "all_eligible",
    label: "All eligible contacts",
    shortLabel: "All Eligible",
    narrativeFragment: "eligible contacts",
    description: "Every active contact passing delivery guardrails.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 7,
    source: "email_contacts",
    manualSelection: false,
  },
  {
    key: "never_contacted",
    label: "Never contacted",
    shortLabel: "Never Contacted",
    narrativeFragment: "never-contacted contacts",
    description: "Have never received an email broadcast.",
    segment: "never_contacted",
    defaultExcludeRecent: false,
    defaultDays: 7,
    source: "email_contacts",
    manualSelection: false,
  },
  {
    key: "hot_leads",
    label: "Hot leads",
    shortLabel: "Hot Leads",
    narrativeFragment: "hot leads",
    description: "Tied to hot or urgent inquiries.",
    segment: "hot_leads",
    defaultExcludeRecent: false,
    defaultDays: 7,
    source: "email_contacts",
    manualSelection: false,
  },
  {
    key: "reengage_30",
    label: "Re-engage 30+ days",
    shortLabel: "Re-engage 30+",
    narrativeFragment: "re-engage contacts",
    description: "Not emailed in the last 30 days.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 30,
    source: "email_contacts",
    manualSelection: false,
  },
  {
    key: "imported_list",
    label: "Imported list",
    shortLabel: "Imported List",
    description: "Upload a CSV or Excel file and send to that list only.",
    segment: "",
    defaultExcludeRecent: false,
    defaultDays: 7,
    source: "import",
    manualSelection: false,
  },
  {
    key: "my_contacts",
    label: "My contacts",
    shortLabel: "My Contacts",
    narrativeFragment: "your personal contacts",
    description: "Send to contacts you created in My Contacts.",
    segment: "",
    defaultExcludeRecent: false,
    defaultDays: 7,
    source: "my_contacts",
    manualSelection: false,
  },
  {
    key: "custom_audience",
    label: "Custom audience",
    shortLabel: "Custom Audience",
    description: "Filter and manually review exactly who gets it.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 7,
    source: "email_contacts",
    manualSelection: true,
  },
];

export function getAudiencePreset(key) {
  return AUDIENCE_PRESETS.find((preset) => preset.key === key) ?? AUDIENCE_PRESETS[0];
}

export function isImportAudience(audienceKey, source) {
  return audienceKey === "imported_list" || source === "import";
}

export function isManualAudience(audienceKey) {
  return audienceKey === "custom_audience";
}

export function toAudienceRow(contact) {
  return {
    id: String(contact.id ?? contact._tmp ?? contact.email ?? contact.primary_email ?? contact.recipient_email ?? `row-${Math.random().toString(16).slice(2)}`),
    name: contact.name || contact.display_name || contact.recipient_name || contact.email || contact.primary_email || contact.recipient_email || "",
    email: String(contact.email || contact.primary_email || contact.recipient_email || "").toLowerCase(),
    last_contacted_at: contact.last_contacted_at || contact.last_seen_at || null,
    contact_status: contact.contact_status || null,
    _kind: contact._kind || "email_contact",
    _email_contact_id: contact.id,
    _source: contact.source || "manual",
  };
}

export function normalizePositiveInteger(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }

  return Math.max(1, Math.floor(numeric));
}

export function buildAudiencePlan({
  audienceKey,
  source,
  filters,
  excludeRecent,
  recentDays,
  summary,
}) {
  const preset = getAudiencePreset(audienceKey);
  const preferredLimit = normalizePositiveInteger(filters.preferred_limit);

  if (isImportAudience(audienceKey, source)) {
    return {
      audienceKey,
      source: "import",
      deliveryMode: "raw_list",
      manualSelection: false,
      label: preset.label,
      filters: preferredLimit ? { preferred_limit: preferredLimit } : {},
    };
  }

  const baseFilters = {
    q: filters.q || "",
    source: filters.source || null,
    active: true,
    segment: preset.segment || filters.segment || "",
    exclude_recent: excludeRecent,
    days: Number(recentDays || preset.defaultDays || 7),
  };

  if (preferredLimit) {
    baseFilters.preferred_limit = preferredLimit;
  }

  if (audienceKey === "my_contacts") {
    baseFilters.audience_kind = "my_contacts";
  }
  // Fold the sender's own MyContacts into the "All eligible" segment only.
  if (audienceKey === "all_eligible") {
    baseFilters.include_my_contacts = true;
  }

  if (preset.manualSelection) {
    return {
      audienceKey,
      source: "email_contacts",
      deliveryMode: "page",
      manualSelection: true,
      label: preset.label,
      filters: {
        ...baseFilters,
        limit: Number(filters.limit || 200),
      },
    };
  }

  return {
    audienceKey,
    source: "email_contacts",
    deliveryMode: "filter_all",
    manualSelection: false,
    label: preset.label,
    filters: baseFilters,
  };
}

export function buildAudienceNarrative({
  audienceKey,
  source,
  filters,
  summary,
}) {
  const preset = getAudiencePreset(audienceKey);
  const sendable = Number(summary?.eligibleFound || 0);
  const matched = Number(summary?.totalFound || sendable || 0);
  const suppressed = Number(summary?.suppressedFound || 0);
  const preferredLimit = normalizePositiveInteger(summary?.preferredLimit || filters?.preferred_limit);
  const preferredSuppressed = Number(summary?.suppressedBreakdown?.["Preferred campaign limit reached"] || 0);

  if (isImportAudience(audienceKey, source)) {
    const limitSentence = preferredSuppressed > 0 && preferredLimit
      ? ` ${formatCount(preferredSuppressed)} are being held back by your preferred send limit of ${formatCount(preferredLimit)}.`
      : "";
    return `This campaign will use ${formatCount(sendable)} contact${sendable === 1 ? "" : "s"} from your imported list.${suppressed > 0 ? ` ${formatCount(suppressed)} will be skipped by delivery guardrails.` : ""}${limitSentence}`;
  }

  const sourceLabel = filters?.source ? sourceLabelFor(filters.source) : "all connected sources";
  const sendSentence = preset.manualSelection
    ? `You are building a custom audience from ${sourceLabel}.`
    : `This campaign will send to ${formatCount(sendable)} ${preset.narrativeFragment || preset.label.toLowerCase()} from ${sourceLabel}.`;
  const preferredSentence = preferredSuppressed > 0 && preferredLimit
    ? ` ${formatCount(preferredSuppressed)} more are being held back by your preferred send limit of ${formatCount(preferredLimit)}.`
    : preferredLimit
      ? ` Preferred send limit: ${formatCount(preferredLimit)}.`
      : "";

  if (preset.manualSelection) {
    return `${sendSentence} ${matched > 0 ? `${formatCount(matched)} matched contacts are ready for review.` : "Run Find Audience to review matching contacts."}${preferredSentence}`;
  }

  if (suppressed > 0) {
    return `${sendSentence} ${formatCount(suppressed)} will be skipped because of recent-contact or delivery guardrails.${preferredSentence}`;
  }

  return `${sendSentence}${preferredSentence}`;
}

export function buildAudienceStatusBadges(summary) {
  return {
    matched: Number(summary?.totalFound || 0),
    sendable: Number(summary?.eligibleFound || 0),
    skipped: Number(summary?.suppressedFound || 0),
  };
}

function sourceLabelFor(source) {
  return {
    hubspot: "HubSpot",
    google: "Google Contacts",
    inquiries: "Inquiries",
    import: "imports",
    manual: "manual contacts",
  }[source] || source;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}
